import styled from 'styled-components';
import { StatusTone } from 'screening/statusLabels';

interface Option<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

interface SegmentedToggleProps<T extends string> {
  options: Option<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Colours the active segment with a status wash instead of plain paper. */
  activeTone?: StatusTone;
  fullWidth?: boolean;
}

const toneColor = (tone: StatusTone) =>
  tone === 'advance'
    ? 'var(--screening-advance)'
    : tone === 'hold'
    ? 'var(--screening-hold)'
    : tone === 'reject'
    ? 'var(--screening-reject)'
    : 'var(--screening-ink)';

const toneWash = (tone: StatusTone) =>
  tone === 'advance'
    ? 'var(--screening-advance-wash)'
    : tone === 'hold'
    ? 'var(--screening-hold-wash)'
    : tone === 'reject'
    ? 'var(--screening-reject-wash)'
    : 'var(--screening-paper)';

const Track = styled.div<{ $fullWidth?: boolean }>`
  display: ${({ $fullWidth }) => ($fullWidth ? 'grid' : 'inline-flex')};
  grid-auto-flow: ${({ $fullWidth }) => ($fullWidth ? 'column' : 'unset')};
  grid-auto-columns: ${({ $fullWidth }) => ($fullWidth ? '1fr' : 'unset')};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  padding: 3px;
  gap: 3px;
  background: var(--screening-canvas);
  border: 1px solid var(--screening-hairline);
  border-radius: var(--screening-r-input);
`;

const Segment = styled.button<{ $active: boolean; $tone?: StatusTone }>`
  border: none;
  background: ${({ $active, $tone }) =>
    $active
      ? $tone
        ? toneWash($tone)
        : 'var(--screening-paper)'
      : 'transparent'};
  color: ${({ $active, $tone }) =>
    $active
      ? $tone
        ? toneColor($tone)
        : 'var(--screening-ink)'
      : 'var(--screening-muted)'};
  font-family: var(--screening-font-ui);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 3px;
  cursor: pointer;
  transition:
    background 160ms ease,
    color 160ms ease;
  box-shadow: ${({ $active, $tone }) =>
    $active && !$tone ? '0 1px 2px rgba(4, 26, 77, 0.08)' : 'none'};

  &:hover:not(:disabled) {
    color: ${({ $tone }) =>
      $tone ? toneColor($tone) : 'var(--screening-ink)'};
  }

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
`;

function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  activeTone,
  fullWidth,
}: SegmentedToggleProps<T>) {
  return (
    <Track role="tablist" $fullWidth={fullWidth}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <Segment
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={opt.disabled}
            $active={active}
            $tone={active ? activeTone : undefined}
            onClick={() => {
              if (!opt.disabled) onChange(opt.value);
            }}
          >
            {opt.label}
          </Segment>
        );
      })}
    </Track>
  );
}

export default SegmentedToggle;
